import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { PencilIcon, TrashIcon, DocumentArrowDownIcon, PlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductionHistory = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [productions, setProductions] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  
  const [filters, setFilters] = useState({
    search: '',
    recipe_id: '',
    date_from: '',
    date_to: ''
  });

  useEffect(() => {
    loadRecipes();
    loadProductions();
  }, []);

  const loadRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipes(response.data);
    } catch (error) {
      console.error('Errore caricamento ricette:', error);
    }
  };

  const loadProductions = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.recipe_id) params.recipe_id = filters.recipe_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      const response = await axios.get(`${API}/productions`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductions(response.data);
    } catch (error) {
      console.error('Errore caricamento produzioni:', error);
      toast.error('Errore nel caricamento delle produzioni');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadProductions();
  };

  const handleDownloadPDF = async (productionId, batchNumber) => {
    try {
      const response = await axios.get(
        `${API}/productions/${productionId}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Produzione_${batchNumber.replace(/\s/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('PDF scaricato!');
    } catch (error) {
      console.error('Errore download PDF:', error);
      toast.error('Errore nel download del PDF');
    }
  };

  const handleEdit = (production) => {
    setEditingId(production.production_id);
    setEditForm({
      completion_date: production.completion_date || '',
      actual_bottles: production.actual_bottles || '',
      notes: production.notes || ''
    });
  };

  const handleSaveEdit = async (productionId) => {
    try {
      await axios.put(
        `${API}/productions/${productionId}`,
        editForm,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setEditingId(null);
      loadProductions();
      toast.success('Produzione aggiornata!');
    } catch (error) {
      console.error('Errore aggiornamento:', error);
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDelete = async (productionId, batchNumber) => {
    if (!window.confirm(`Eliminare la produzione ${batchNumber}?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/productions/${productionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      loadProductions();
      toast.success('Produzione eliminata!');
    } catch (error) {
      console.error('Errore eliminazione:', error);
      toast.error('Errore nell\'eliminazione');
    }
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Storico Produzioni</h1>
          <p className="text-gray-600">Visualizza e gestisci tutte le produzioni</p>
        </div>
        <Button onClick={() => navigate('/productions/new')}>
          <PlusIcon className="w-5 h-5 mr-2" />
          Nuova Produzione
        </Button>
      </div>

      {/* Filtri */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MagnifyingGlassIcon className="w-5 h-5" />
            Filtri di Ricerca
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Ricerca</Label>
              <Input
                placeholder="Lotto, cliente, operatore..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>

            <div className="space-y-2">
              <Label>Ricetta</Label>
              <select
                value={filters.recipe_id}
                onChange={(e) => setFilters({...filters, recipe_id: e.target.value})}
                className="w-full p-2 border rounded-md"
              >
                <option value="">Tutte</option>
                {recipes.map(recipe => (
                  <option key={recipe.recipe_id} value={recipe.recipe_id}>
                    {recipe.recipe_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label>Data Da</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({...filters, date_from: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>Data A</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({...filters, date_to: e.target.value})}
              />
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <Button onClick={handleSearch}>
              Cerca
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setFilters({ search: '', recipe_id: '', date_from: '', date_to: '' });
                setTimeout(loadProductions, 100);
              }}
            >
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabella Produzioni */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Caricamento...</p>
        </div>
      ) : productions.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-gray-500 mb-4">Nessuna produzione trovata</p>
            <Button onClick={() => navigate('/productions/new')}>
              Crea Prima Produzione
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {productions.map(production => (
            <Card key={production.production_id}>
              <CardContent className="pt-6">
                {editingId === production.production_id ? (
                  // Modalità Edit
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Data Completamento</Label>
                        <Input
                          type="date"
                          value={editForm.completion_date}
                          onChange={(e) => setEditForm({...editForm, completion_date: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Bottiglie Effettive</Label>
                        <Input
                          type="number"
                          value={editForm.actual_bottles}
                          onChange={(e) => setEditForm({...editForm, actual_bottles: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Note</Label>
                        <Input
                          value={editForm.notes}
                          onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => handleSaveEdit(production.production_id)} size="sm">
                        Salva
                      </Button>
                      <Button onClick={() => setEditingId(null)} variant="outline" size="sm">
                        Annulla
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Modalità Visualizzazione
                  <div>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold">{production.batch_number}</h3>
                          <Badge variant="outline">{production.recipe_name}</Badge>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Cliente:</span> {production.customer}
                          </div>
                          <div>
                            <span className="font-medium">Operatore:</span> {production.operator}
                          </div>
                          <div>
                            <span className="font-medium">Data:</span> {production.production_date}
                          </div>
                          <div>
                            <span className="font-medium">Formato:</span> {production.bottle_format_cl} cl
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadPDF(production.production_id, production.batch_number)}
                          title="Scarica PDF"
                        >
                          <DocumentArrowDownIcon className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(production)}
                          title="Modifica"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(production.production_id, production.batch_number)}
                          title="Elimina"
                          className="text-red-600 hover:text-red-700"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Quantità</p>
                        <p className="font-bold text-lg">
                          {production.quantity_bottles} bottiglie
                        </p>
                        <p className="text-xs text-gray-500">
                          ({production.total_liters} L totali)
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Costo Totale</p>
                        <p className="font-bold text-lg text-blue-600">
                          € {production.total_cost.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">
                          Ing: €{production.cost_breakdown.ingredients.toFixed(2)} | 
                          Pkg: €{production.cost_breakdown.packaging.toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Stato Completamento</p>
                        {production.completion_date ? (
                          <div>
                            <Badge className="bg-green-500">Completata</Badge>
                            <p className="text-xs text-gray-500 mt-1">
                              {production.completion_date}
                              {production.actual_bottles && ` • ${production.actual_bottles} bott.`}
                            </p>
                          </div>
                        ) : (
                          <Badge variant="outline">In Corso</Badge>
                        )}
                      </div>
                    </div>

                    {production.notes && (
                      <div className="mt-3 p-3 bg-yellow-50 rounded text-sm">
                        <span className="font-medium">Note:</span> {production.notes}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductionHistory;
